import { useMaterials } from '$/hooks/data/useMaterials';
import { type FavoriteItem } from '$/pages/DashboardPages/pages/Favorite';
import { DEFAULTFOLDERNAME, useFavoriteStore } from '$/stores/useFavoriteStore';

export const useGroupedFavorites = (
  parentFolder: string,
  materialOrder: string[] = [],
) => {
  const favorites = useFavoriteStore.useFavorites();
  const favoriteIds = favorites.map((favorite) => favorite.materialId);

  const [{ isLoading }, , materials] = useMaterials({
    ids: favoriteIds,
    limit: 1000,
  });

  const hasNoMatchingMaterials =
    parentFolder === DEFAULTFOLDERNAME && materials?.length === 0;

  if (isLoading || hasNoMatchingMaterials || favorites.length === 0) {
    return {
      isLoading,
      allFavorites: [] as FavoriteItem[],
      colorFavorites: [] as FavoriteItem[],
      wallFavorites: [] as FavoriteItem[],
      floorFavorites: [] as FavoriteItem[],
      facadeFavorites: [] as FavoriteItem[],
    };
  }

  const sortedFavorites = favorites.sort((a, b) => {
    let aIndex = materialOrder.indexOf(a.materialId);
    let bIndex = materialOrder.indexOf(b.materialId);

    if (aIndex === -1 && bIndex === -1) {
      return a.materialId.localeCompare(b.materialId);
    }

    aIndex = aIndex === -1 ? 1000 : aIndex;
    bIndex = bIndex === -1 ? 1000 : bIndex;
    return aIndex - bIndex;
  });

  const allFavorites = sortedFavorites.reduce<FavoriteItem[]>(
    (existingFavorites, current) => {
      if (!current.parentFolderIds.includes(parentFolder))
        return existingFavorites;

      const material = materials?.find(
        (material) => material?.id === current.materialId,
      );

      if (material != null) {
        existingFavorites.push({
          material,
          parentFolders: current.parentFolderIds,
        } as FavoriteItem);
      }

      return existingFavorites;
    },
    [],
  );

  const colorFavorites = allFavorites.filter(
    (favorite) => favorite.material.type[0] === 'color_tint',
  );

  const wallFavorites = allFavorites.filter(
    (favorite) => favorite.material.type[0] === 'wall_texture',
  );

  const floorFavorites = allFavorites.filter(
    (favorite) => favorite.material.type[0] === 'floor_texture',
  );

  const facadeFavorites = allFavorites.filter(
    (favorite) => favorite.material.type[0] === 'facade_texture',
  );

  return {
    isLoading: false,
    allFavorites,
    colorFavorites,
    wallFavorites,
    floorFavorites,
    facadeFavorites,
  };
};
